import React from 'react';

export default function UseCasesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Use Cases</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Use Case ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Représente une fonctionnalité métier spécifique</li>
                                <li>Orchestre les interactions entre composants du domaine</li>
                                <li>Gère les transactions et la gestion d'erreurs</li>
                                <li>Indépendant de l'infrastructure</li>
                                <li>Définit un contrat clair entre l'interface et le domaine</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure d'un Use Case</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`// 1. Command (entrée)
public record CreatePaymentCommand(
    Amount amount,
    String debtorIban,
    String creditorIban,
    LocalDate executionDate,
    String reference
) {}

// 2. Port (interface)
public interface CreatePaymentUseCase {
    PaymentResult createPayment(CreatePaymentCommand command);
}

// 3. Implémentation
@UseCase
@Transactional
public class CreatePaymentService implements CreatePaymentUseCase {
    private final PaymentDomainService domainService;
    private final PaymentRepository paymentRepository;
    private final AccountPort accountPort;

    @Override
    public PaymentResult createPayment(CreatePaymentCommand command) {
        try {
            // 1. Validation des données d'entrée
            validateCommand(command);

            // 2. Création du paiement (domaine)
            Payment payment = Payment.builder()
                .amount(command.amount())
                .debtor(accountPort.getAccount(command.debtorIban()))
                .creditor(accountPort.getAccount(command.creditorIban()))
                .executionDate(command.executionDate())
                .reference(command.reference())
                .build();

            // 3. Validation du domaine
            domainService.validatePayment(payment);

            // 4. Persistance
            payment = paymentRepository.save(payment);

            // 5. Résultat
            return PaymentResult.success(payment);

        } catch (ValidationException e) {
            return PaymentResult.validationError(e.getErrors());
        } catch (Exception e) {
            return PaymentResult.technicalError(e);
        }
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples de Use Cases</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Processus de Paiement</h4>
                                <pre className="text-xs mt-2">
{`@UseCase
public class ProcessPaymentUseCase {
    private final PaymentDomainService domainService;
    private final PaymentGatewayPort gatewayPort;
    private final NotificationPort notificationPort;

    @Transactional
    public ProcessingResult processPayment(UUID paymentId) {
        try {
            // 1. Récupération du paiement
            Payment payment = paymentRepository.findById(paymentId)
                .orElseThrow(() -> new PaymentNotFoundException(paymentId));

            // 2. Validation du processus
            domainService.validateProcessing(payment);

            // 3. Envoi au système de paiement
            GatewayResponse response = gatewayPort.sendPayment(payment);

            // 4. Mise à jour du statut
            payment.updateStatus(response.getStatus());
            paymentRepository.save(payment);

            // 5. Notification
            notificationPort.notifyPaymentStatus(
                payment.getId(), 
                payment.getStatus()
            );

            return ProcessingResult.success(payment);

        } catch (Exception e) {
            return ProcessingResult.failure(e);
        }
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Validation de Mandat</h4>
                                <pre className="text-xs mt-2">
{`@UseCase
public class ValidateMandateUseCase {
    private final MandateDomainService domainService;
    private final CustomerPort customerPort;
    private final NotificationPort notificationPort;

    @Transactional
    public ValidationResult validateMandate(
        ValidateMandateCommand command
    ) {
        try {
            // 1. Vérification du client
            Customer customer = customerPort.getCustomer(
                command.customerId()
            );

            // 2. Création du mandat
            Mandate mandate = Mandate.builder()
                .reference(command.reference())
                .debtor(customer)
                .creditor(command.creditor())
                .startDate(command.startDate())
                .build();

            // 3. Validation domaine
            domainService.validateMandate(mandate);

            // 4. Activation
            mandate.activate();
            mandateRepository.save(mandate);

            // 5. Notification
            notificationPort.notifyMandateValidation(
                mandate.getReference()
            );

            return ValidationResult.success(mandate);

        } catch (Exception e) {
            return ValidationResult.failure(e);
        }
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. CQRS Pattern</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`// Command
@UseCase
public class CreatePaymentUseCase {
    public CommandResult createPayment(CreatePaymentCommand cmd) {
        // Logique de création
    }
}

// Query
@UseCase
public class GetPaymentDetailsUseCase {
    public PaymentDetails getPayment(GetPaymentQuery query) {
        // Logique de lecture
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Gestion des Erreurs</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@UseCase
public class RobustPaymentUseCase {
    public PaymentResult processPayment(ProcessPaymentCommand cmd) {
        try {
            // Logique normale
            return PaymentResult.success(payment);
        } catch (ValidationException e) {
            return PaymentResult.validation(e.getErrors());
        } catch (BusinessException e) {
            return PaymentResult.business(e.getReason());
        } catch (TechnicalException e) {
            return PaymentResult.technical(e);
        }
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Caractéristiques Essentielles</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Indépendance</h4>
                            <p className="text-sm text-gray-600">
                                Indépendant de l'infrastructure et des détails techniques
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Orchestration</h4>
                            <p className="text-sm text-gray-600">
                                Coordonne les interactions entre les différents composants
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Gestion des Erreurs</h4>
                            <p className="text-sm text-gray-600">
                                Gère de manière cohérente les erreurs et exceptions
                            </p>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Transactions</h4>
                            <p className="text-sm text-gray-600">
                                Assure la cohérence transactionnelle des opérations
                            </p>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Validation</h4>
                            <p className="text-sm text-gray-600">
                                Valide les entrées avant de les transmettre au domaine
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}